import { CompanionBoundary } from '@/features/companion/CompanionRoutes';
import { useStudyToolTelemetry } from '@/lib/useStudyToolTelemetry';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useKidToken } from '@/auth/authStore';
import { isExplicitParentStudio, isParentStudioPath } from '@/auth/parentStudio';
import { useMe } from '@/auth/useAuth';
import {
  clearActiveStudyAssignment,
  syncActiveStudyAssignmentWithRoute,
} from '@/lib/studyAssignmentContext';
import { sendWsEvent } from '@/lib/ws';
import { useProductInteractionTracking, useProductOpenTracking } from '@/lib/productOpenAnalytics';
import { NudgeBanner } from '@/pages/learn/liveClass/NudgeBanner';
import { reEmitFocus } from '@/pages/learn/liveClass/reportFocus';
import { StudyToolRouteGate } from '@/pages/learn/study/StudyToolRouteGate';
import { StudentPaymentPermissionHost } from '@/pages/learn/student/StudentPaymentPermissionHost';

import { ParentStudioBanner } from './ParentStudioBanner';
import { LearnTopBar } from './LearnTopBar';
import { isStudyMaterialReader } from './learnNavigation';
import { LearnDock } from './LearnDock';
import { LearnPageFrame } from './LearnPageFrame';

const HEARTBEAT_INTERVAL_MS = 10_000;

// Routes that need to render full-bleed (no centered max-width container).
// Workspace is the IDE-style 3-pane surface and must use 100% width; the
// playground is the Phaser game-studio desktop (fills the area below the nav).
const FLUID_ROUTES = [
  '/learn/workspace',
  '/learn/code',
  '/learn/playground',
  '/learn/blocks',
  '/learn/video',
];

// IMMERSIVE routes take over the whole viewport: NO nav bar, no page scroll —
// the surface manages its own layout. Creative Code Studio and Story Blocks
// both provide their own Home control in the bottom taskbar. The
// hub `/learn/create/blocks` keeps the nav; only the studio `/learn/blocks/:id`
// is immersive.
// The Art Studio is a full-screen easel like the Blocks studio (owner call
// 2026-07-20: 肯定是全屏,就跟 Story Blocks 一样) — image-studio-prd v0.14.
// Only the canvas is immersive: the `/learn/create/image` hub keeps the kid
// navigation and reading-column scroll, like `/learn/create/blocks`.
const IMMERSIVE_ROUTES = [
  '/learn/companion',
  '/learn/playground/',
  '/learn/blocks/',
  '/learn/music',
  '/learn/create/image/canvas',
  '/learn/video/',
  '/learn/study',
];

// Of the immersive surfaces, only the Art Studio canvas ALSO asks the browser
// for OS fullscreen on the first tap. The Music Stage is immersive (no nav, no
// page scroll) but stays a normal browser page — a desktop kid composing a song
// shouldn't have their whole browser hijacked into fullscreen (user decision,
// music-stage-prd D-MS7 refinement). Story Blocks stopped auto-entering
// fullscreen too (owner decision 2026-10-03, story-blocks-living-storybook-prd
// D-LSB-22): it stays immersive, but the browser is not taken over.
const AUTO_FULLSCREEN_ROUTES = ['/learn/create/image/canvas'];

// Immersive shells that are still ordinary browser pages. Study renders its own
// full-width shell, but it is reading and tools, not a drawing canvas, so it must
// keep the browser's trackpad swipe back/forward. `overscroll-behavior: none` on
// <html> switches that gesture off, so these routes never get the gesture lock.
const BROWSER_GESTURE_ROUTES = ['/learn/study', '/learn/companion'];

// Walk-in (unclaimed) workshop kids only see their class + their kid code
// (auth-system-prd §5.2): the top-level catalog surfaces below bounce to the
// classroom. DEEP working routes stay open — a class mission still launches the
// studios (/learn/blocks/:id, /learn/code/:id, /learn/playground/:id), project
// details and mission details — so nothing in the workshop itself is blocked.
// Exported for tests. (Pre-existing pattern: data/util export beside the layout
// component — the fast-refresh warning is benign here, same as LearnTopBar's
// NAV_ITEMS, and would otherwise fail the deploy's --max-warnings 0 lint.)
// eslint-disable-next-line react-refresh/only-export-components
export function isRestrictedForWalkIn(pathname: string): boolean {
  if (pathname === '/learn' || pathname === '/learn/' || pathname.startsWith('/learn/companion'))
    return true;
  if (
    pathname === '/learn/projects' ||
    pathname === '/learn/projects/new' ||
    pathname === '/learn/my'
  )
    return true;
  if (pathname === '/learn/missions') return true;
  if (pathname === '/learn/create' || pathname.startsWith('/learn/create/')) return true;
  if (pathname === '/learn/workspace' || pathname.startsWith('/learn/workspace/')) return true;
  return false;
}

export function LearnLayout() {
  useStudyToolTelemetry();
  const location = useLocation();
  const { pathname } = location;
  const kidToken = useKidToken();
  const parentMode =
    isExplicitParentStudio(pathname, location.search) ||
    (kidToken === null && isParentStudioPath(pathname));
  useProductOpenTracking(parentMode ? 'user' : 'kid');
  useProductInteractionTracking(parentMode ? 'user' : 'kid');
  // SIR-VIS-04: assignment tagging lives only on the assigned tool route, entered from the task.
  useEffect(() => {
    syncActiveStudyAssignmentWithRoute(location.pathname, location.state);
  }, [location.pathname, location.state]);
  useEffect(() => () => clearActiveStudyAssignment(), []);
  const immersive =
    IMMERSIVE_ROUTES.some((p) => pathname.startsWith(p)) ||
    /^\/learn\/exams\/[^/]+\/practice\/?$/.test(pathname);
  // Immersive IMPLIES full-bleed. Listing a route as immersive but forgetting the
  // FLUID list hid the nav and locked page scroll while still wrapping the surface
  // in the centered max-w-5xl box — a "fullscreen" studio letterboxed inside a
  // reading column. There is no case where an immersive surface wants that box.
  const companionPage = pathname.startsWith('/learn/companion');
  const fluid =
    immersive || FLUID_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const lockGestures = immersive && !BROWSER_GESTURE_ROUTES.some((p) => pathname.startsWith(p));

  // Depend on the kid token so the FIRST heartbeat (which lazily creates + connects
  // the kid socket via getSocket) fires the moment auth lands — not up to 10s later
  // — and reconnects on token rotation. Without this, the bootstrap refresh resolves
  // after mount and the socket stays down until the next interval tick.
  // Parent studio entries keep the adult surface even on a shared browser:
  // no child navigation, heartbeat or nudge banner.
  useEffect(() => {
    if (parentMode) return undefined; // no kid socket → nothing to heartbeat
    const tick = () => {
      sendWsEvent('class.heartbeat', { ts: Date.now() });
      // Re-emit the kid's currently-open project (D-LIVE-3) so a teacher who
      // opens Live Mode mid-session syncs within ~10s. No-op when nothing open.
      reEmitFocus();
    };
    tick();
    const id = window.setInterval(tick, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [kidToken, parentMode]);

  // Immersive surfaces (Blocks Studio) own the whole viewport: lock page scroll
  // and request browser fullscreen on the first gesture. This lives in the LAYOUT
  // — which stays mounted across the studio page's re-renders/remounts (e.g. the
  // periodic auth refresh briefly swapping the route out) — and keys off the
  // route. Previously the studio component owned this, so any transient remount
  // dropped fullscreen (unmount cleanup) and re-armed the one-shot enter, making
  // it flicker out and snap back on the next tap. Keyed on `immersive`, a studio
  // remount no longer touches it. If the user/browser leaves fullscreen, we don't
  // yank them back.
  useEffect(() => {
    if (!immersive) return undefined;
    const html = document.documentElement;
    const prevOverflow = document.body.style.overflow;
    const prevOverscroll = html.style.overscrollBehavior;
    document.body.style.overflow = 'hidden';
    if (lockGestures) html.style.overscrollBehavior = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      html.style.overscrollBehavior = prevOverscroll;
    };
  }, [immersive, lockGestures]);

  // OS fullscreen on the first gesture — Art Studio canvas only. Story Blocks
  // and the Music Stage are immersive but must NOT take the browser into
  // fullscreen; see AUTO_FULLSCREEN_ROUTES above.
  const autoFullscreen = AUTO_FULLSCREEN_ROUTES.some((p) => pathname.startsWith(p));
  useEffect(() => {
    if (!autoFullscreen) return undefined;
    const html = document.documentElement;
    let dismissed = false; // user/browser left fullscreen → don't auto re-enter
    const enter = () => {
      if (!dismissed && !document.fullscreenElement && html.requestFullscreen) {
        void html.requestFullscreen().catch(() => undefined);
      }
    };
    const onFsChange = () => {
      if (!document.fullscreenElement) dismissed = true;
    };
    window.addEventListener('pointerdown', enter, { once: true });
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      window.removeEventListener('pointerdown', enter);
      document.removeEventListener('fullscreenchange', onFsChange);
      if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => undefined);
    };
  }, [autoFullscreen]);

  // Walk-in restriction (§5.2): declared after every hook so the early return
  // can't break hook order. While /auth/me is still loading we render normally —
  // the server scopes a walk-in session to its class regardless.
  const me = useMe('kid');
  const isWalkIn = me.data?.kind === 'kid' && me.data.is_ephemeral === true;
  if (isWalkIn && isRestrictedForWalkIn(pathname)) {
    return <Navigate to="/learn/classroom" replace />;
  }

  return (
    <CompanionBoundary kid={me.data?.kind === 'kid' && !parentMode ? me.data : null}>
      <LearnPageFrame
        immersive={immersive}
        fluid={fluid}
        banner={parentMode ? <ParentStudioBanner /> : <NudgeBanner />}
        navigation={!immersive && !parentMode && <LearnTopBar />}
        dock={
          !parentMode &&
          (!immersive ||
            companionPage ||
            (pathname.startsWith('/learn/study') && !isStudyMaterialReader(pathname))) && (
            <LearnDock inFlow={companionPage} />
          )
        }
      >
        {/* One-way teacher nudge banner (D-LIVE-2) — hosted here (like the heartbeat)
          so it surfaces across every Learn surface, including immersive studios.
          Parent studio mode renders neither the banner nor the kid top bar. */}
        {/* Immersive main is flex-1, NOT h-full: h-full is 100% of the whole layout
          column, so the moment something renders above it (the NudgeBanner — which
          deliberately surfaces over studios) the studio was pushed down and its
          bottom bar clipped off-screen by exactly the banner height. flex-1 hands
          the studio the space that is actually left. */}
        <StudyToolRouteGate enabled={!parentMode}>
          <Outlet />
        </StudyToolRouteGate>
        {/* D2 (student-self-signup-prd.md §19): the one-time "I may pay" step an
          independent student sees before their first purchase. Renders nothing
          until a checkout is refused with STUDENT_PAYMENT_PERMISSION_REQUIRED. */}
        {!parentMode && <StudentPaymentPermissionHost />}
      </LearnPageFrame>
    </CompanionBoundary>
  );
}
